// frontend/src/App.js
import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './context/AuthContext';
import { CurrencyProvider } from './context/CurrencyContext';
import { EnvironmentProvider } from './context/EnvironmentContext';
import { WesScopeProvider } from './context/WesScopeContext';
import { EntitlementsProvider, EntitlementGate } from './context/EntitlementsContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { RouteErrorBoundary, PageErrorBoundary } from './components/common/ErrorBoundaryWrappers';
import LoadingSpinner from './components/common/LoadingSpinner';
import { routePreloader } from './utils/performanceOptimizations';
import LanguageDetectionService from './utils/languageDetection';
import { useTranslation } from 'react-i18next';
import './i18n/config';
import { HUB_ROUTE_ADMIN_ROLE_LIST, HUB_ROUTE_ADMIN_WES_ROLE_LIST } from './constants/roles';
import { loadMockState } from './mock/store';

// Layout components - keep these imported directly as they're used frequently.
import AppShell from './components/layout/AppShell';
import ProtectedRoute from './components/auth/ProtectedRoute';
import TenantRoute from './components/auth/TenantRoute';
import TenantRedirect from './components/auth/TenantRedirect';
import OrganizationRoute from './components/auth/OrganizationRoute';

// Critical pages - keep these imported directly for better initial load
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import NotFoundPage from './pages/NotFoundPage';
// Eager: small page shell; lazy chunk for this route often fails (ChunkLoadError) under
// docker bind-mount + webpack dev server when the async chunk 404s or desyncs from main.
import IdpPage from './pages/IdpPage';

// Admin pages - lazy loaded for security
const AdminDashboardPage = React.lazy(() => import('./pages/AdminDashboardPage'));
const AdminAIAuditPage = React.lazy(() => import('./pages/AdminAIAuditPage'));

// Add ResetPasswordPage and ForgotPasswordPage as critical pages since users might need them immediately
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));

// Lazy load admin and less frequently used pages
const ExecutorsPage = React.lazy(() => import('./pages/ExecutorsPage'));
const MachinesPage = React.lazy(() => import('./pages/MachinesPage'));
const CreateMachinePage = React.lazy(() => import('./pages/CreateMachinePage'));
const AutomationsPage = React.lazy(() => import('./pages/AutomationsPage'));
const PackagesPage = React.lazy(() => import('./pages/PackagesPage'));
const SchedulesPage = React.lazy(() => import('./pages/SchedulesPage'));
const QueuesPage = React.lazy(() => import('./pages/QueuesPage'));
const LoginDebugPage = React.lazy(() => import('./pages/LoginDebugPage'));
const CreateJobPage = React.lazy(() => import('./pages/CreateJobPage'));
const CreateSchedulePage = React.lazy(() => import('./pages/CreateSchedulePage'));
const UploadPackagePage = React.lazy(() => import('./pages/UploadPackagePage'));
const RegisterExecutorPage = React.lazy(() => import('./pages/RegisterExecutorPage'));
const DebugPage = React.lazy(() => import('./pages/DebugPage'));
const UserDebugPage = React.lazy(() => import('./pages/UserDebugPage'));
const UsersPage = React.lazy(() => import('./pages/UsersPage'));
const AdminSkillsPage = React.lazy(() => import('./pages/AdminSkillsPage'));
const CompanyManagementPage = React.lazy(() => import('./pages/CompanyManagementPage'));
const OrganizationsManagementPage = React.lazy(() => import('./pages/OrganizationsManagementPage'));
const SectorsManagementPage = React.lazy(() => import('./pages/SectorsManagementPage'));
const UsersProtoPage = React.lazy(() => import('./pages/UsersProtoPage'));
const CompaniesProtoPage = React.lazy(() => import('./pages/CompaniesProtoPage'));
const OrganizationPage = React.lazy(() => import('./pages/OrganizationPage'));
const EnvironmentPage = React.lazy(() => import('./pages/EnvironmentPage'));
const CredentialsPage = React.lazy(() => import('./pages/CredentialsPage'));
// AuditPage retired: /audit redirects to unified AdminAIAuditPage (/ai-audit).
const InputFilesPage = React.lazy(() => import('./pages/InputFilesPage'));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const ProfilePage = React.lazy(() => import('./pages/ProfilePage'));
const TenantSelectionPage = React.lazy(() => import('./pages/TenantSelectionPage'));
const TutorialPage = React.lazy(() => import('./pages/TutorialPage'));
const EmbeddedPage = React.lazy(() => import('./pages/EmbeddedPage'));

// Mock IAM portal (dev only)
const MockShell = React.lazy(() => import('./components/mock/MockShell'));
const MockPortalPage = React.lazy(() => import('./pages/MockPortalPage'));
const MockTenantsPage = React.lazy(() => import('./pages/MockTenantsPage'));
const MockAdmClienteAccessPage = React.lazy(() => import('./pages/MockAdmClienteAccessPage'));
const MockSectorsPage = React.lazy(() => import('./pages/MockSectorsPage'));
const MockUserAccessPage = React.lazy(() => import('./pages/MockUserAccessPage'));
const MockSectionPage = React.lazy(() => import('./pages/MockSectionPage'));
const FluxosPage = React.lazy(() => import('./pages/FluxosPage'));
const InsightsPreviewPage = React.lazy(() => import('./pages/InsightsPreviewPage'));
const AIAgentsPage = React.lazy(() => import('./pages/AIAgentsPage'));
const SkillsPage = React.lazy(() => import('./pages/SkillsPage'));
const PublicAgentPage = React.lazy(() => import('./pages/PublicAgentPage'));
const PublicAgentPreviewPage = React.lazy(() => import('./pages/debug/PublicAgentPreviewPage'));
const VoiceMessagingPage = React.lazy(() => import('./pages/VoiceMessagingPage'));
const VoiceMessagingWizardPage = React.lazy(() => import('./pages/VoiceMessagingWizardPage'));
const VoiceMessagingDetailPage = React.lazy(() => import('./pages/VoiceMessagingDetailPage'));
const VoiceMessagingInsightsPage = React.lazy(() => import('./pages/VoiceMessagingInsightsPage'));
const IntegrationsPage = React.lazy(() => import('./pages/IntegrationsPage'));
const McpManagementPage = React.lazy(() => import('./pages/McpManagementPage'));
const McpConfigurePage = React.lazy(() => import('./pages/McpConfigurePage'));
const TelegramIntegrationFormPage = React.lazy(() => import('./pages/TelegramIntegrationFormPage'));
const TelegramBotFatherGuidePage = React.lazy(() => import('./pages/TelegramBotFatherGuidePage'));
const WabaIntegrationFormPage = React.lazy(() => import('./pages/WabaIntegrationFormPage'));
const CampaignsPage = React.lazy(() => import('./pages/CampaignsPage'));
const CampaignFormPage = React.lazy(() => import('./pages/CampaignFormPage'));
const CampaignDetailPage = React.lazy(() => import('./pages/CampaignDetailPage'));
const HybridFlowsListPage = React.lazy(() => import('./pages/automation/HybridFlowsListPage'));
const HybridFlowEditorPage = React.lazy(() => import('./pages/automation/HybridFlowEditorPage'));
const HybridFlowSessionsPage = React.lazy(() => import('./pages/automation/HybridFlowSessionsPage'));
const HybridFlowVoiceStartPage = React.lazy(() => import('./pages/public/HybridFlowVoiceStartPage'));
const HybridFlowConfirmPage = React.lazy(() => import('./pages/public/HybridFlowConfirmPage'));

// Transitional alias — Phase 4 will rename the two route slots that still
// reference <Dashboard /> and drop this line.
const Dashboard = AppShell;

// Wrapper component for lazy-loaded pages with loading state.
// When `loadingMessage` is set (i18n string from parent), the Suspense fallback is localised.
const LazyPageWrapper = ({ children, pageName, loadingMessage }) => (
  <Suspense
    fallback={(
      <LoadingSpinner
        message={loadingMessage ?? `Loading ${pageName}...`}
        center
      />
    )}
  >
    <PageErrorBoundary pageName={pageName}>
      {children}
    </PageErrorBoundary>
  </Suspense>
);

// App content component that uses the theme
function AppContent() {
  const { theme } = useTheme();
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const useMockAuth = process.env.NODE_ENV !== 'production' && process.env.REACT_APP_USE_MOCK_AUTH === 'true';
  const mockTenantSlug = (() => {
    try {
      const s = loadMockState();
      const tenant = (s?.tenants || []).find((x) => x.id === s?.session?.tenantId);
      const name = tenant?.name || 'Empresa';
      return name.toLowerCase().replace(/\s+/g, '-');
    } catch (e) {
      return 'empresa';
    }
  })();

  // Initialize language detection on app start
  useEffect(() => {
    const initializeLanguage = async () => {
      try {
        // Only auto-detect on first visit (no stored preference)
        if (!localStorage.getItem('skipper_language')) {
          console.log('First visit detected, initializing language detection...');
          
          const detection = await LanguageDetectionService.initializeLanguage();
          if (!detection) {
            return;
          }
          
          if (detection.source === 'geolocation' && detection.language !== i18n.language) {
            console.log(`Auto-switching to ${detection.language} based on location: ${detection.location?.country}`);
            i18n.changeLanguage(detection.language);
          }
        }
      } catch (error) {
        console.error('Language initialization failed:', error);
        // Fallback to default language handling
      }
    };

    // Initialize language detection
    initializeLanguage();
  }, [i18n]);

  // Preload common routes after initial load
  useEffect(() => {
    // Preload common routes with a delay to not impact initial performance
    const timer = setTimeout(() => {
      routePreloader.preloadCommon();
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  // Preload routes based on current location
  useEffect(() => {
    const currentPath = location.pathname;
    
    // Preload related routes based on current page
    if (currentPath.includes('/packages')) {
      routePreloader.preloadMultiple(['uploadPackage', 'automations']);
    } else if (currentPath.includes('/automations')) {
      routePreloader.preloadMultiple(['createJob', 'packages', 'schedules']);
    } else if (currentPath.includes('/users')) {
      routePreloader.preloadMultiple(['organization', 'settings']);
    } else if (currentPath.includes('/dashboard') || currentPath === '/') {
      routePreloader.preloadMultiple(['packages', 'automations', 'settings']);
    }
  }, [location.pathname]);

  const renderHybridFlowsRoutes = () => (
    <>
      <Route
        path="automation/hybrid-flows"
        element={
          <ProtectedRoute requiredPermissions={['hybrid_flows:read']}>
            <LazyPageWrapper
              pageName="Hybrid Flows"
              loadingMessage={t('hybridFlows.suspense.list')}
            >
              <HybridFlowsListPage />
            </LazyPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="automation/hybrid-flows/new"
        element={
          <ProtectedRoute requiredPermissions={['hybrid_flows:write']}>
            <LazyPageWrapper
              pageName="New Hybrid Flow"
              loadingMessage={t('hybridFlows.suspense.editorNew')}
            >
              <HybridFlowEditorPage />
            </LazyPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="automation/hybrid-flows/:flowId"
        element={
          <ProtectedRoute requiredPermissions={['hybrid_flows:write']}>
            <LazyPageWrapper
              pageName="Hybrid Flow"
              loadingMessage={t('hybridFlows.suspense.editor')}
            >
              <HybridFlowEditorPage />
            </LazyPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="automation/hybrid-flows/:flowId/sessions"
        element={
          <ProtectedRoute requiredPermissions={['hybrid_flows:read']}>
            <LazyPageWrapper
              pageName="Hybrid Flow Sessions"
              loadingMessage={t('hybridFlows.suspense.sessions')}
            >
              <HybridFlowSessionsPage />
            </LazyPageWrapper>
          </ProtectedRoute>
        }
      />
    </>
  );

  const renderIdpRoute = () => (
    <Route
      path="automation/idp"
      element={
        <ProtectedRoute>
          <EntitlementGate
            feature="document_analysis"
            level="product"
            fallback={
              <LazyPageWrapper pageName="Not Found">
                <NotFoundPage />
              </LazyPageWrapper>
            }
          >
            <LazyPageWrapper pageName="WES IDP" loadingMessage={t('idpPage.suspense')}>
              <IdpPage />
            </LazyPageWrapper>
          </EntitlementGate>
        </ProtectedRoute>
      }
    />
  );

  const renderIntegrationRoutes = () => (
    <>
      <Route
        path="integrations"
        element={
          <ProtectedRoute
            requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST}
            requiredPermissions={['tenant:read']}
          >
            <LazyPageWrapper pageName="Integrations">
              <IntegrationsPage />
            </LazyPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="mcps"
        element={
          <ProtectedRoute
            requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST}
            requiredPermissions={['tenant:read']}
          >
            <LazyPageWrapper pageName="MCPs">
              <McpManagementPage />
            </LazyPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="mcps/:mcpId/configure"
        element={
          <ProtectedRoute
            requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST}
            requiredPermissions={['tenant:update']}
          >
            <LazyPageWrapper pageName="MCP Configuration">
              <McpConfigurePage />
            </LazyPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="integrations/telegram/botfather"
        element={
          <ProtectedRoute
            requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST}
            requiredPermissions={['tenant:update']}
          >
            <LazyPageWrapper pageName="Telegram BotFather Guide">
              <TelegramBotFatherGuidePage />
            </LazyPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="integrations/telegram/new"
        element={
          <ProtectedRoute
            requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST}
            requiredPermissions={['tenant:update']}
          >
            <LazyPageWrapper pageName="Telegram Integration">
              <TelegramIntegrationFormPage />
            </LazyPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="integrations/telegram/:integrationId"
        element={
          <ProtectedRoute
            requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST}
            requiredPermissions={['tenant:update']}
          >
            <LazyPageWrapper pageName="Telegram Integration">
              <TelegramIntegrationFormPage />
            </LazyPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="integrations/waba/new"
        element={
          <ProtectedRoute
            requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST}
            requiredPermissions={['tenant:update']}
          >
            <LazyPageWrapper pageName="WABA Integration">
              <WabaIntegrationFormPage />
            </LazyPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="integrations/waba/:integrationId"
        element={
          <ProtectedRoute
            requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST}
            requiredPermissions={['tenant:update']}
          >
            <LazyPageWrapper pageName="WABA Integration">
              <WabaIntegrationFormPage />
            </LazyPageWrapper>
          </ProtectedRoute>
        }
      />
    </>
  );

  const renderCampaignRoutes = () => (
    <>
      <Route
        path="campaigns"
        element={
          <ProtectedRoute
            requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST}
            requiredPermissions={['campaigns:read']}
          >
            <LazyPageWrapper pageName="Campaigns">
              <CampaignsPage />
            </LazyPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="campaigns/new"
        element={
          <ProtectedRoute
            requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST}
            requiredPermissions={['campaigns:write']}
          >
            <LazyPageWrapper pageName="New Campaign">
              <CampaignFormPage />
            </LazyPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="campaigns/:campaignId"
        element={
          <ProtectedRoute
            requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST}
            requiredPermissions={['campaigns:read']}
          >
            <LazyPageWrapper pageName="Campaigns">
              <CampaignsPage />
            </LazyPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="campaigns/:campaignId/recipients"
        element={
          <ProtectedRoute
            requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST}
            requiredPermissions={['campaigns:read']}
          >
            <LazyPageWrapper pageName="Campaign">
              <CampaignDetailPage />
            </LazyPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="campaigns/:campaignId/summary"
        element={
          <ProtectedRoute
            requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST}
            requiredPermissions={['campaigns:read']}
          >
            <LazyPageWrapper pageName="Campaign">
              <CampaignDetailPage />
            </LazyPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="campaigns/:campaignId/insights"
        element={
          <ProtectedRoute
            requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST}
            requiredPermissions={['campaigns:read']}
          >
            <LazyPageWrapper pageName="Campaign">
              <CampaignDetailPage />
            </LazyPageWrapper>
          </ProtectedRoute>
        }
      />
      <Route
        path="campaigns/:campaignId/edit"
        element={
          <ProtectedRoute
            requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST}
            requiredPermissions={['campaigns:write']}
          >
            <LazyPageWrapper pageName="Edit Campaign">
              <CampaignFormPage />
            </LazyPageWrapper>
          </ProtectedRoute>
        }
      />
    </>
  );

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <EntitlementsProvider>
        <CurrencyProvider>
          <EnvironmentProvider>
            <WesScopeProvider>
            <Routes>
          {/* Landing page - public */}
          <Route path="/" element={
            <Navigate to="/login" replace />
          } />
          
          {/* Public routes */}
          {/*
            Published AI agent experience — rendered outside ProtectedRoute so
            that anonymous external visitors (no JWT, no CSRF cookie) can
            consume a published agent via slug. Backed by the
            ``/api/v1/public/agents/*`` endpoints which share the same CSRF
            exemption on the server side. See
            wes-hub-360/docs/plans/2026-04-23-publicar-agentes-externos-design.md.
          */}
          <Route path="/public/agent/:slug" element={
            <LazyPageWrapper pageName="Public Agent">
              <PublicAgentPage />
            </LazyPageWrapper>
          } />
          {process.env.NODE_ENV !== 'production' && (
            <Route path="/debug/public-agent-preview" element={
              <LazyPageWrapper pageName="Public Agent Preview">
                <PublicAgentPreviewPage />
              </LazyPageWrapper>
            } />
          )}
          <Route path="/login" element={
            <PageErrorBoundary pageName="Login">
              <LoginPage />
            </PageErrorBoundary>
          } />
          {/* Hybrid flow external entry (US #2226 CA-3). Outside the
              tenant shell because the visitor is unauthenticated. */}
          <Route path="/p/hf/:token" element={
            <LazyPageWrapper
              pageName="Hybrid Flow Voice Start"
              loadingMessage={t('hybridFlows.suspense.publicVoice')}
            >
              <HybridFlowVoiceStartPage />
            </LazyPageWrapper>
          } />
          {/* Confirmation-via-link page (US #2226 - confirm-via-link UX).
              Mounted here, sibling to /p/hf/:token, so the bot link
              renders outside the tenant shell. */}
          <Route path="/p/hf/c/:confirmToken" element={
            <LazyPageWrapper
              pageName="Hybrid Flow Confirm"
              loadingMessage={t('hybridFlows.suspense.publicConfirm')}
            >
              <HybridFlowConfirmPage />
            </LazyPageWrapper>
          } />
          <Route path="/tutorial" element={
            <LazyPageWrapper pageName="Tutorial">
              <TutorialPage />
            </LazyPageWrapper>
          } />

          {/* Mock IAM portal - dev only */}
          {process.env.NODE_ENV !== 'production' && (
            <Route
              path="/mock"
              element={
                <LazyPageWrapper pageName="Mock IAM">
                  <MockShell />
                </LazyPageWrapper>
              }
            >
              <Route
                index
                element={
                  <LazyPageWrapper pageName="Mock IAM - Overview">
                    <MockPortalPage />
                  </LazyPageWrapper>
                }
              />
              <Route
                path="empresas"
                element={
                  <LazyPageWrapper pageName="Mock IAM - Empresas">
                    <MockTenantsPage />
                  </LazyPageWrapper>
                }
              />
              <Route
                path="acessos-adm-cliente"
                element={
                  <LazyPageWrapper pageName="Mock IAM - Acessos ADM Cliente">
                    <MockAdmClienteAccessPage />
                  </LazyPageWrapper>
                }
              />
              <Route
                path="setores"
                element={
                  <LazyPageWrapper pageName="Mock IAM - Setores">
                    <MockSectorsPage />
                  </LazyPageWrapper>
                }
              />
              <Route
                path="acessos-usuarios"
                element={
                  <LazyPageWrapper pageName="Mock IAM - Acessos Usuários">
                    <MockUserAccessPage />
                  </LazyPageWrapper>
                }
              />
              <Route
                path="secao/:sectionKey"
                element={
                  <LazyPageWrapper pageName="Mock IAM - Seção">
                    <MockSectionPage />
                  </LazyPageWrapper>
                }
              />
            </Route>
          )}

          {/* Admin routes - protected */}
          <Route path="/admin" element={
            <ProtectedRoute>
              <LazyPageWrapper pageName="Admin Dashboard">
                <AdminDashboardPage />
              </LazyPageWrapper>
            </ProtectedRoute>
          } />
          {/* NOTE: AI Audit is tenant-scoped under /:tenant/ai-audit (see below). */}
          <Route path="/forgot-password" element={
            <LazyPageWrapper pageName="Forgot Password">
              <ForgotPasswordPage />
            </LazyPageWrapper>
          } />
          <Route path="/reset-password" element={
            <LazyPageWrapper pageName="Reset Password">
              <ResetPasswordPage />
            </LazyPageWrapper>
          } />
          <Route path="/unauthorized" element={
            <PageErrorBoundary pageName="Unauthorized">
              <UnauthorizedPage />
            </PageErrorBoundary>
          } />
          <Route path="/login-debug" element={
            <LazyPageWrapper pageName="Login Debug">
              <LoginDebugPage />
            </LazyPageWrapper>
          } />
          <Route path="/debug" element={
            <LazyPageWrapper pageName="Debug">
              <DebugPage />
            </LazyPageWrapper>
          } />
          <Route path="/user-debug" element={
            <LazyPageWrapper pageName="User Debug">
              <UserDebugPage />
            </LazyPageWrapper>
          } />

          {/* AI Insights Preview Page - for testing before production */}
          <Route path="/insights-preview" element={
            <LazyPageWrapper pageName="Insights Preview">
              <InsightsPreviewPage />
            </LazyPageWrapper>
          } />

          {/* Tenant selection - protected route */}
          <Route 
            path="/select-tenant" 
            element={
              <ProtectedRoute>
                <LazyPageWrapper pageName="Tenant Selection">
                  <TenantSelectionPage />
                </LazyPageWrapper>
              </ProtectedRoute>
            } 
          />
          {/* Tombstones for the retired commercial surface. Keep these
              explicit so dynamic organization/tenant routes cannot claim
              former public URLs. */}
          <Route path="/pricing" element={<NotFoundPage />} />
          <Route path="/about" element={<NotFoundPage />} />
          <Route path="/blog" element={<NotFoundPage />} />
          <Route path="/blog/*" element={<NotFoundPage />} />
          <Route path="/register" element={<NotFoundPage />} />
          <Route path="/verify-email" element={<NotFoundPage />} />
          <Route path="/subscription" element={<NotFoundPage />} />
          <Route path="/subscription/tiers" element={<NotFoundPage />} />
          <Route path="/subscription/*" element={<NotFoundPage />} />

          {/* Redirect old dashboard URL to tenant-based URL */}
          <Route 
            path="/dashboard/*" 
            element={
              <ProtectedRoute>
                <TenantRedirect />
              </ProtectedRoute>
            } 
          />
          
          {/* Organization-based routes */}
          <Route 
            path="/:orgName" 
            element={
              <OrganizationRoute>
                <RouteErrorBoundary>
                  <AppShell />
                </RouteErrorBoundary>
              </OrganizationRoute>
            }
          >
            <Route index element={
              <PageErrorBoundary pageName="Dashboard">
                <HomePage />
              </PageErrorBoundary>
            } />

            <Route path="subscription" element={<NotFoundPage />} />
            <Route path="subscription/*" element={<NotFoundPage />} />
            
            {/* All the same nested routes as tenant routes */}
            <Route 
              path="agents" 
              element={
                <ProtectedRoute requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST} requiredPermissions={['agent:read']}>
                  <LazyPageWrapper pageName="Agents">
                    <ExecutorsPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />
            <Route 
              path="executors" 
              element={
                <ProtectedRoute requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST} requiredPermissions={['agent:read']}>
                  <LazyPageWrapper pageName="Executors">
                    <ExecutorsPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />
            <Route 
              path="agents/register" 
              element={
                <ProtectedRoute requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST} requiredPermissions={['agent:create']}>
                  <LazyPageWrapper pageName="Register Agent">
                    <RegisterExecutorPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />

            <Route
              path="ai-audit"
              element={
                <ProtectedRoute requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST}>
                  <LazyPageWrapper pageName="AI Audit">
                    <AdminAIAuditPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />
            <Route 
              path="executors/register" 
              element={
                <ProtectedRoute requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST} requiredPermissions={['agent:create']}>
                  <LazyPageWrapper pageName="Register Executor">
                    <RegisterExecutorPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />

            {/* New Machine Management Routes */}
            <Route 
              path="machines" 
              element={
                <ProtectedRoute requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST} requiredPermissions={['agent:read']}>
                  <LazyPageWrapper pageName="Machines">
                    <MachinesPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />
            <Route 
              path="machines/create" 
              element={
                <ProtectedRoute requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST} requiredPermissions={['agent:create']}>
                  <LazyPageWrapper pageName="Create Machine">
                    <CreateMachinePage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />
            <Route 
              path="executors/create" 
              element={
                <ProtectedRoute requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST} requiredPermissions={['agent:create']}>
                  <LazyPageWrapper pageName="Create Executor">
                    <CreateMachinePage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />
            
            <Route 
              path="automations" 
              element={
                <ProtectedRoute requiredPermissions={['job:read']}>
                  <LazyPageWrapper pageName="Automations">
                    <AutomationsPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="automations/create" 
              element={
                <ProtectedRoute requiredPermissions={['job:create']}>
                  <LazyPageWrapper pageName="Create Job">
                    <CreateJobPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="jobs" 
              element={<Navigate to="automations" replace />}
            />
            <Route 
              path="jobs/create" 
              element={<Navigate to="automations/create" replace />}
            />
            
            <Route 
              path="packages" 
              element={
                <ProtectedRoute requiredPermissions={['package:read']}>
                  <LazyPageWrapper pageName="Packages">
                    <PackagesPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="packages/upload" 
              element={
                <ProtectedRoute requiredPermissions={['package:create']}>
                  <LazyPageWrapper pageName="Upload Package">
                    <UploadPackagePage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="schedules" 
              element={
                <ProtectedRoute requiredPermissions={['schedule:read']}>
                  <LazyPageWrapper pageName="Schedules">
                    <SchedulesPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="schedules/create" 
              element={
                <ProtectedRoute requiredPermissions={['schedule:create']}>
                  <LazyPageWrapper pageName="Create Schedule">
                    <CreateSchedulePage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="queues" 
              element={
                <ProtectedRoute requiredPermissions={['queue:read']}>
                  <LazyPageWrapper pageName="Queues">
                    <QueuesPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              } 
            />

            {renderIntegrationRoutes()}
            {renderCampaignRoutes()}
            {renderHybridFlowsRoutes()}
            {renderIdpRoute()}

            <Route 
              path="credentials" 
              element={
                <ProtectedRoute>
                  <LazyPageWrapper pageName="Credentials">
                    <CredentialsPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="input-files" 
              element={
                <ProtectedRoute>
                  <LazyPageWrapper pageName="Input Files">
                    <InputFilesPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              } 
            />
            
            <Route
              path="users"
              element={
                <ProtectedRoute requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST}>
                  <LazyPageWrapper pageName="Users">
                    {process.env.NODE_ENV !== 'production' && process.env.REACT_APP_USE_MOCK_AUTH === 'true' ? (
                      <UsersProtoPage />
                    ) : (
                      <UsersPage />
                    )}
                  </LazyPageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route
              path="skills"
              element={
                <ProtectedRoute requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST}>
                  <LazyPageWrapper pageName="Skills">
                    <AdminSkillsPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="companies"
              element={
                <ProtectedRoute requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST}>
                  <LazyPageWrapper pageName="Empresas">
                    {process.env.NODE_ENV !== 'production' && process.env.REACT_APP_USE_MOCK_AUTH === 'true' ? (
                      <CompaniesProtoPage />
                    ) : (
                      <CompanyManagementPage />
                    )}
                  </LazyPageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route
              path="organizations"
              element={
                <EntitlementGate
                  feature="organizations_admin"
                  level="product"
                  fallback={<Navigate to="../" replace />}
                >
                  <ProtectedRoute requiredRoles={HUB_ROUTE_ADMIN_WES_ROLE_LIST}>
                    <LazyPageWrapper pageName="Organizações">
                      <OrganizationsManagementPage />
                    </LazyPageWrapper>
                  </ProtectedRoute>
                </EntitlementGate>
              }
            />
            <Route 
              path="sectors" 
              element={
                <ProtectedRoute requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST}>
                  <LazyPageWrapper pageName="Sectors">
                    <SectorsManagementPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route path="audit" element={<Navigate to="ai-audit" replace />} />
            <Route 
              path="organization" 
              element={
                <ProtectedRoute requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST}>
                  <LazyPageWrapper pageName="Organization">
                    <OrganizationPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="environments" 
              element={
                <ProtectedRoute requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST}>
                  <LazyPageWrapper pageName="Environments">
                    <EnvironmentPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              } 
            />
            
            <Route
              path="profile"
              element={
                <ProtectedRoute>
                  <LazyPageWrapper pageName="Profile">
                    <ProfilePage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />

            <Route
              path="settings"
              element={
                <ProtectedRoute>
                  <LazyPageWrapper pageName="Settings">
                    <SettingsPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />

            <Route
              path="bpmn"
              element={
                <ProtectedRoute>
                  <LazyPageWrapper pageName="BPMN">
                    <EmbeddedPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />

            <Route
              path="fluxos"
              element={
                <ProtectedRoute>
                  <LazyPageWrapper pageName="Fluxos">
                    <FluxosPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />

            <Route
              path="ai-agents"
              element={
                <ProtectedRoute requiredPermissions={['job:read']}>
                  <LazyPageWrapper pageName="AI Agents">
                    <AIAgentsPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="agent-skills"
              element={
                <ProtectedRoute requiredPermissions={['skill:read']}>
                  <LazyPageWrapper pageName="Skills">
                    <SkillsPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />

            <Route
              path="voice-messaging"
              element={
                <ProtectedRoute requiredPermissions={['job:read']}>
                  <LazyPageWrapper pageName="Voice Messaging">
                    <VoiceMessagingPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="voice-messaging/create"
              element={
                <ProtectedRoute requiredPermissions={['job:create']}>
                  <LazyPageWrapper pageName="New Voice Messaging">
                    <VoiceMessagingWizardPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="voice-messaging/insights"
              element={
                <ProtectedRoute requiredPermissions={['job:read']}>
                  <LazyPageWrapper pageName="Voice Messaging Insights">
                    <VoiceMessagingInsightsPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="voice-messaging/:operationId/edit"
              element={
                <ProtectedRoute requiredPermissions={['job:create']}>
                  <LazyPageWrapper pageName="Resume Voice Messaging">
                    <VoiceMessagingWizardPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="voice-messaging/:operationId"
              element={
                <ProtectedRoute requiredPermissions={['job:read']}>
                  <LazyPageWrapper pageName="Voice Messaging Detail">
                    <VoiceMessagingDetailPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Protected routes - tenant-based (fallback) */}
          <Route 
            path="/:tenant/*" 
            element={
              <ProtectedRoute>
                <TenantRoute>
                  <RouteErrorBoundary>
                    <AppShell />
                  </RouteErrorBoundary>
                </TenantRoute>
              </ProtectedRoute>
            }
          >
            <Route index element={
              <PageErrorBoundary pageName="Dashboard">
                <HomePage />
              </PageErrorBoundary>
            } />

            <Route path="subscription" element={<NotFoundPage />} />
            <Route path="subscription/*" element={<NotFoundPage />} />
            
            {/* Agents - requires admin role or agent:read permission */}
            <Route 
              path="agents" 
              element={
                <ProtectedRoute requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST} requiredPermissions={['agent:read']}>
                  <LazyPageWrapper pageName="Agents">
                    <ExecutorsPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />
            <Route 
              path="executors" 
              element={
                <ProtectedRoute requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST} requiredPermissions={['agent:read']}>
                  <LazyPageWrapper pageName="Executors">
                    <ExecutorsPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />
            <Route 
              path="agents/register" 
              element={
                <ProtectedRoute requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST} requiredPermissions={['agent:create']}>
                  <LazyPageWrapper pageName="Register Agent">
                    <RegisterExecutorPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />
            <Route 
              path="executors/register" 
              element={
                <ProtectedRoute requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST} requiredPermissions={['agent:create']}>
                  <LazyPageWrapper pageName="Register Executor">
                    <RegisterExecutorPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />
            
            {/* Automations - requires job:read permission */}
            <Route 
              path="automations" 
              element={
                <ProtectedRoute requiredPermissions={['job:read']}>
                  <LazyPageWrapper pageName="Automations">
                    <AutomationsPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="automations/create" 
              element={
                <ProtectedRoute requiredPermissions={['job:create']}>
                  <LazyPageWrapper pageName="Create Job">
                    <CreateJobPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              } 
            />
            {/* Keep backward compatibility for old jobs URLs */}
            <Route 
              path="jobs" 
              element={<Navigate to="automations" replace />}
            />
            <Route 
              path="jobs/create" 
              element={<Navigate to="automations/create" replace />}
            />
            
            {/* Packages - requires package:read permission */}
            <Route 
              path="packages" 
              element={
                <ProtectedRoute requiredPermissions={['package:read']}>
                  <LazyPageWrapper pageName="Packages">
                    <PackagesPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="packages/upload" 
              element={
                <ProtectedRoute requiredPermissions={['package:create']}>
                  <LazyPageWrapper pageName="Upload Package">
                    <UploadPackagePage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              } 
            />
            
            {/* Schedules - requires schedule:read permission */}
            <Route 
              path="schedules" 
              element={
                <ProtectedRoute requiredPermissions={['schedule:read']}>
                  <LazyPageWrapper pageName="Schedules">
                    <SchedulesPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="schedules/create" 
              element={
                <ProtectedRoute requiredPermissions={['schedule:create']}>
                  <LazyPageWrapper pageName="Create Schedule">
                    <CreateSchedulePage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              } 
            />
            
            {/* Queues - requires queue:read permission */}
            <Route 
              path="queues" 
              element={
                <ProtectedRoute requiredPermissions={['queue:read']}>
                  <LazyPageWrapper pageName="Queues">
                    <QueuesPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              } 
            />

            {renderIntegrationRoutes()}
            {renderCampaignRoutes()}
            {renderHybridFlowsRoutes()}
            {renderIdpRoute()}

            {/* Storage - requires user authentication */}
            <Route 
              path="credentials" 
              element={
                <ProtectedRoute>
                  <LazyPageWrapper pageName="Credentials">
                    <CredentialsPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="input-files" 
              element={
                <ProtectedRoute>
                  <LazyPageWrapper pageName="Input Files">
                    <InputFilesPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              } 
            />
            
            {/* Admin - requires admin role */}
            <Route
              path="users"
              element={
                <ProtectedRoute requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST}>
                  <LazyPageWrapper pageName="Users">
                    <UsersPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route
              path="skills"
              element={
                <ProtectedRoute requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST}>
                  <LazyPageWrapper pageName="Skills">
                    <AdminSkillsPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="ai-audit"
              element={
                <ProtectedRoute requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST}>
                  <LazyPageWrapper pageName="AI Audit">
                    <AdminAIAuditPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />
            <Route path="audit" element={<Navigate to="ai-audit" replace />} />
            <Route 
              path="organization" 
              element={
                <ProtectedRoute requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST}>
                  <LazyPageWrapper pageName="Organization">
                    <OrganizationPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="environments" 
              element={
                <ProtectedRoute requiredRoles={HUB_ROUTE_ADMIN_ROLE_LIST}>
                  <LazyPageWrapper pageName="Environments">
                    <EnvironmentPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              } 
            />
            
            {/* Settings - requires authentication */}
            <Route
              path="profile"
              element={
                <ProtectedRoute>
                  <LazyPageWrapper pageName="Profile">
                    <ProfilePage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />

            <Route
              path="settings"
              element={
                <ProtectedRoute>
                  <LazyPageWrapper pageName="Settings">
                    <SettingsPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />

            {/* BPMN - user configurable external page */}
            <Route
              path="bpmn"
              element={
                <ProtectedRoute>
                  <LazyPageWrapper pageName="BPMN">
                    <EmbeddedPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />

            {/* Fluxos - n8n workflow automation */}
            <Route
              path="fluxos"
              element={
                <ProtectedRoute>
                  <LazyPageWrapper pageName="Fluxos">
                    <FluxosPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />

            {/* AI Agents - RAG-powered AI agents */}
            <Route
              path="ai-agents"
              element={
                <ProtectedRoute requiredPermissions={['job:read']}>
                  <LazyPageWrapper pageName="AI Agents">
                    <AIAgentsPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="agent-skills"
              element={
                <ProtectedRoute requiredPermissions={['skill:read']}>
                  <LazyPageWrapper pageName="Skills">
                    <SkillsPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />

            {/* Voice Messaging - mocked voice messaging operations */}
            <Route
              path="voice-messaging"
              element={
                <ProtectedRoute requiredPermissions={['job:read']}>
                  <LazyPageWrapper pageName="Voice Messaging">
                    <VoiceMessagingPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="voice-messaging/create"
              element={
                <ProtectedRoute requiredPermissions={['job:create']}>
                  <LazyPageWrapper pageName="New Voice Messaging">
                    <VoiceMessagingWizardPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="voice-messaging/insights"
              element={
                <ProtectedRoute requiredPermissions={['job:read']}>
                  <LazyPageWrapper pageName="Voice Messaging Insights">
                    <VoiceMessagingInsightsPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="voice-messaging/:operationId/edit"
              element={
                <ProtectedRoute requiredPermissions={['job:create']}>
                  <LazyPageWrapper pageName="Resume Voice Messaging">
                    <VoiceMessagingWizardPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />
            <Route
              path="voice-messaging/:operationId"
              element={
                <ProtectedRoute requiredPermissions={['job:read']}>
                  <LazyPageWrapper pageName="Voice Messaging Detail">
                    <VoiceMessagingDetailPage />
                  </LazyPageWrapper>
                </ProtectedRoute>
              }
            />
          </Route>

          {/* Fallback routes */}
          <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </WesScopeProvider>
          </EnvironmentProvider>
        </CurrencyProvider>
        </EntitlementsProvider>
      </AuthProvider>
    </MuiThemeProvider>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}

export default App;

